/**
 * Serial Data Parsing Utilities
 *
 * Shared parsing functions for serial device data (Bluetooth scales, RFID readers)
 * Used by both web (DeviceContext) and mobile (useBluetoothScale)
 *
 * Supported formats:
 * - Animal ID: 15 digit numeric (RFID tags)
 * - Tru-Test: [S123.45], [U123.45], [Z123.45]
 * - EziWeigh: [WS123.45]
 * - Generic: ST,123.45 kg, GS,123.45 kg, etc.
 */

// Regex patterns for different data formats

// Helper compartido de formateo locale-aware (mismo módulo en web y mobile)
import { formatWeight as sharedFormatWeight } from './formatNumber';

export const WEIGHT_FORMATS = {
  /** Tru-Test format: [S123.45] - S=Stable, U=Unstable, Z=Zero */
  TRU_TEST: /^\[([USZ])([+-]?\d+\.?\d*)\]$/,

  /** EziWeigh format: [WS123.45] */
  EZIWEIGH: /^\[WS([+-]?\d+\.?\d*)\]$/,

  /** Generic weight format: ST,123.45 kg, GS,123.45, etc. */
  GENERIC: /(?:ST|GS|NT)?[,]*([+-]?\s*\d+\.?\d*)\s*(kg|KG|Kg|lb|LB)?/,

  /** Animal ID: 15 digit numeric (RFID tags) */
  ANIMAL_ID: /^\d{15}$/
} as const;

/**
 * Parsed serial data result
 */
export interface ParsedSerialData {
  /** Type of data detected */
  type: 'animal_id' | 'weight' | 'unknown';

  /** Parsed value (ID string or weight number as string) */
  value: string;

  /** Original raw data */
  raw: string;

  /** Weight status (only for weight type)
   * - 'U': Underweight/Unstable
   * - 'S': Stable
   * - 'Z': Zero
   * - 'WS': EziWeigh stable
   */
  status?: 'U' | 'S' | 'Z' | 'WS';
}

/**
 * Parse incoming serial data and determine its type
 *
 * @param data - Raw string data from serial device
 * @returns ParsedSerialData with type, value, and status
 *
 * @example
 * parseSerialData('[S450.5]')  // { type: 'weight', value: '450.5', status: 'S' }
 * parseSerialData('982000123456789')  // { type: 'animal_id', value: '982000123456789' }
 * parseSerialData('ST,123.45 kg')  // { type: 'weight', value: '123.45', status: 'S' }
 */
export function parseSerialData(data: string): ParsedSerialData {
  const cleaned = data.trim();
  const numericOnly = cleaned.replace(/\s/g, '');

  // Check for Animal ID (15 digits)
  if (WEIGHT_FORMATS.ANIMAL_ID.test(numericOnly)) {
    return {
      type: 'animal_id',
      value: numericOnly,
      raw: data
    };
  }

  // Check for Tru-Test format [S123.45], [U123.45], [Z123.45]
  const truTestMatch = cleaned.match(WEIGHT_FORMATS.TRU_TEST);
  if (truTestMatch) {
    const weightValue = truTestMatch[2];
    const formattedWeight = weightValue.includes('.') ? weightValue : weightValue + '.0';
    return {
      type: 'weight',
      value: formattedWeight,
      raw: data,
      status: truTestMatch[1] as 'U' | 'S' | 'Z'
    };
  }

  // Check for EziWeigh format [WS123.45]
  const eziweighMatch = cleaned.match(WEIGHT_FORMATS.EZIWEIGH);
  if (eziweighMatch) {
    return {
      type: 'weight',
      value: eziweighMatch[1],
      raw: data,
      status: 'WS'
    };
  }

  // Check for generic weight format
  const weightMatch = cleaned.match(WEIGHT_FORMATS.GENERIC);
  if (weightMatch) {
    const weightValue = weightMatch[1].replace(/\s/g, '');
    return {
      type: 'weight',
      value: weightValue,
      raw: data,
      status: 'S'
    };
  }

  // Unknown format
  return {
    type: 'unknown',
    value: cleaned,
    raw: data
  };
}

/**
 * Parse data specifically as an animal ID
 *
 * @param data - Raw string data
 * @returns Animal ID if valid 15-digit format, null otherwise
 */
export function parseAnimalId(data: string): string | null {
  const cleaned = data.trim();
  const numericOnly = cleaned.replace(/\s/g, '');

  if (WEIGHT_FORMATS.ANIMAL_ID.test(numericOnly)) {
    return numericOnly;
  }

  return null;
}

/**
 * Parse data specifically as a weight value
 *
 * @param data - Raw string data
 * @returns Object with value and optional status, or null if not a valid weight
 */
export function parseWeight(data: string): { value: string; status?: string } | null {
  const cleaned = data.trim();

  // Check for Tru-Test format
  const truTestMatch = cleaned.match(WEIGHT_FORMATS.TRU_TEST);
  if (truTestMatch) {
    const weightValue = truTestMatch[2];
    const formattedWeight = weightValue.includes('.') ? weightValue : weightValue + '.0';
    return {
      value: formattedWeight,
      status: truTestMatch[1]
    };
  }

  // Check for EziWeigh format
  const eziweighMatch = cleaned.match(WEIGHT_FORMATS.EZIWEIGH);
  if (eziweighMatch) {
    return {
      value: eziweighMatch[1],
      status: 'WS'
    };
  }

  // Check for generic weight format
  const weightMatch = cleaned.match(WEIGHT_FORMATS.GENERIC);
  if (weightMatch) {
    const weightValue = weightMatch[1].replace(/\s/g, '');
    return {
      value: weightValue,
      status: 'S'
    };
  }

  return null;
}

/**
 * Check if a weight status indicates a stable reading
 *
 * @param status - Weight status from parsing
 * @returns true if the weight is stable and reliable
 */
export function isStableWeight(status?: string): boolean {
  return status === 'S' || status === 'WS';
}

/**
 * Get a human-readable description for weight status
 *
 * @param status - Weight status from parsing
 * @returns Spanish description of the status
 */
export function getWeightStatusDescription(status?: string): string {
  switch (status) {
    case 'S':
      return 'Estable';
    case 'U':
      return 'Inestable';
    case 'Z':
      return 'En cero';
    case 'WS':
      return 'Estable';
    default:
      return '';
  }
}

/**
 * Format weight value for display
 *
 * @param value - Weight value as string
 * @param unit - Unit to append (default: 'kg')
 * @returns Formatted weight string
 */
export function formatWeight(value: string, unit: string = 'kg'): string {
  const numValue = parseFloat(value);
  if (isNaN(numValue)) {
    return value;
  }
  // Lectura de balanza en vivo: 1 decimal con formato locale-aware (es-AR → coma)
  return sharedFormatWeight(numValue, { decimals: 1, unit });
}
